import type { DocNode } from '@atlaskit/adf-schema';

import type { ConfluencePageInitCommandProperties } from '@atlassian/rovo-content-bridge-api-commands';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';

import { log } from '../utils/aifc-logging';
import { chunkSubstr, getDocument, isEmptyDocument } from '../utils/bridge-document-utils';

import { CommandHandler } from './types/command-handler-types';

const STREAM_DELAY_MS = 2000;

export class PageInitCommandHandler extends CommandHandler<
	ConfluencePageInitCommandProperties,
	void
> {
	async listen(params: ConfluencePageInitCommandProperties): Promise<void> {
		const document = await getDocument(this.properties.editorAPI);
		if (!document) {
			log('PageInitCommand received but document is not available');
			return;
		}

		const isEmpty = isEmptyDocument(document as DocNode);

		log(`Received ConfluencePageInitCommand with isEmpty: ${isEmpty}`, params?.data);

		if (!isEmpty) {
			return;
		}

		const editorSchema = this.properties.editorAPI.core.sharedState.currentState()?.schema;
		if (!editorSchema) {
			log('Editor schema is not available');
			return;
		}

		setTimeout(() => {
			log('Streaming initial ADF from ConfluencePageInitCommand', params?.data.adf);
			this.properties.onStreamStart({ streamType: 'init', params });
			this.properties.onStream({ streamType: 'init', params });
			// In case ADF is invalid, we chunk it to retain as much of the ADF as possible
			const streamer = new ADFStreamer(editorSchema);
			const adfChunks = chunkSubstr(params.data.adf, 50);
			for (const chunk of adfChunks) {
				log('Processing chunk from ConfluencePageInitCommand', chunk);
				streamer.processChunk(chunk);
			}
			const adfDoc = streamer.getLastDoc();
			log('Replacing document with ADF from ConfluencePageInitCommand', adfDoc);
			log('Replace document function - ', this.properties.editorAPI.core.actions.replaceDocument);
			this.properties.editorAPI.core.actions.replaceDocument(adfDoc, { scrollIntoView: false });
			this.properties.onStreamEnd({ streamType: 'init', params });
		}, STREAM_DELAY_MS);
	}
}
